/**
 * CRM Contacts list — searchable table with create/edit, skeleton, debounce.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SearchInput, Badge, EmptyState, Button, SkeletonRow, Pagination } from '../../components/ui';
import { Users, Plus, Mail, Edit3 } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { timeAgo } from '../../lib/utils';
import { usePersistedPageSize } from '../../hooks/use-persisted-page-size';
import { fetchContacts } from '../../lib/crm-api';
import type { CrmContact } from '../../lib/crm-api';
import { ContactFormDialog } from './forms';

export function ContactsPage() {
  const t = useT();
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('crm.contacts', 20);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CrmContact | null>(null);

  // Debounce search
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchContacts({
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      setContacts(res.items);
      setTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, pageSize]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            size="sm"
            className="flex-1 min-w-[120px] sm:flex-none sm:w-[200px]"
            value={search}
            onChange={setSearch}
            placeholder={t('crm.searchContacts')}
          />
          <div className="flex-1" />
          <span className="text-xs text-fg-faint">{total} results</span>
          <Button
            size="sm"
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            <Plus size={14} />
            <span className="hidden sm:inline ml-1">Add Contact</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!loading && contacts.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('crm.noContactsFound')}
            description={debouncedSearch ? t('crm.adjustSearch') : t('crm.createFirstContact')}
          />
        ) : (
          <table className="w-full min-w-[700px] [&_td]:whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-surface-muted [&_th]:whitespace-nowrap">
              <tr className="border-b border-edge bg-surface-muted">
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Name</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Email</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">Title</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Company</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">
                  Last Contact
                </th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Source</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
                : contacts.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => (window.location.hash = `#/crm/contacts/${c.id}`)}
                      className="border-b border-edge hover:bg-surface-muted cursor-pointer transition-colors group"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-surface-muted flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-fg-muted">
                              {c.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-fg truncate" title={c.name}>
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <Mail size={11} className="text-fg-faint flex-shrink-0" />
                          <span className="text-xs text-fg-secondary truncate" title={c.email || undefined}>
                            {c.email || '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-xs text-fg-secondary">{c.title || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        {c.company_name ? (
                          <span className="text-xs text-fg-secondary">{c.company_name}</span>
                        ) : (
                          <span className="text-xs text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-[11px] text-fg-muted">
                          {c.last_contacted_at ? timeAgo(c.last_contacted_at) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        {c.source ? (
                          <Badge>{c.source.replace(/_/g, ' ')}</Badge>
                        ) : (
                          <span className="text-xs text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditTarget(c);
                            setFormOpen(true);
                          }}
                          className="p-1 rounded hover:bg-surface-muted text-fg-faint hover:text-fg-secondary opacity-0 group-hover:opacity-100 touch-visible transition-opacity"
                          title={t('common.edit')}
                        >
                          <Edit3 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />

      {/* Create/Edit dialog */}
      <ContactFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={loadData} initial={editTarget} />
    </div>
  );
}
